import {type Dispatch} from 'react';

import {
  Alert,
  alpha,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Stack,
  styled,
  Tooltip,
  Typography,
} from '@mui/material/';

import {StyledAdornedEnd} from 'styles/InputMenu';
import InputActionButtons from './InputActionButtons';

import useAddCageHarvest from 'hooks/addEdit/useAddCageHarvest';
import useParseURL from 'hooks/useParseURL';
import NumberFormatCustom from 'helpers/NumberFormatter';

import InfoIcon from '@mui/icons-material/Info';
import {isValidDate, kilosToGrams, kilosToTons, numberDisplay, dateReader} from 'helpers/UnitsFormatHandler';
import {useGetCageHarvests, useGetPlan, useGetSiteCages, useGetSites} from 'hooks/swr';

import {PickersDay, StaticDatePicker} from '@mui/x-date-pickers';
import {DEFAULT_WEIGHT_UNIT, MAX_FISH_WEIGHT_GRAMS} from 'appConstants';
import {type ForecastStateOpenSpeciesEnum, type ForecastState} from 'client';
// import DatePicker from 'components/Generic/DatePicker';
import {GenericSelect, GenericSelectItem} from 'components/Generic/Selectors';
import GenericTextField from 'components/Generic/TextField';
import {CageHarvestRecommendations} from 'components/InputMenu/CageHarvestRecommendations';
import {AUTO_FOCUS_CLASS} from 'components/Navigate/Drawers/RightDrawer';
import {startOfDay} from 'date-fns';
import {useTranslation} from 'react-i18next';
import {
  actionTypeEnum,
  FULL_HARVEST_PERCENTAGE,
  type IAvailability,
  type IHarvest,
  type IHarvestAction,
} from './Harvest/reducer';
import {useGetHogLweFormatter} from 'hooks/useGetHogLweFormatter';
import {useFilterContext} from 'context/FilterContext';
import {WeightUnitSelector} from 'components/Shared/WeightUnitSelector';
import {InputMenuDatePicker} from 'components/Generic/DatePickersV2/InputMenuDatePicker';
import {handleKeyDown} from './helpers';

const EditMenuContainer = ({emergencyHarvest}: {emergencyHarvest?: boolean}) => {
  const {t} = useTranslation('common');
  const filterContext = useFilterContext();
  const {formatLweHog, formatToLwe} = useGetHogLweFormatter();

  const {setCreateUpdateCageHarvest, inputMenuDispatch, availableHarvestPlants, state, dispatch} =
    useAddCageHarvest(emergencyHarvest);

  const {planId} = useParseURL();
  const {data: plan} = useGetPlan({resourceId: planId});
  const {data: sites} = useGetSites({resourceId: planId});
  const {data: cages} = useGetSiteCages({resourceId: state.siteId ? state.siteId : undefined});

  const planStartDate = plan?.startDate ? dateReader(plan?.startDate) : new Date();
  const planEndDate = plan?.endDate ? dateReader(plan?.endDate) : new Date();

  const gradedHarvestError =
    state.forecast &&
    state.isGraded &&
    state.weight !== undefined &&
    !!(state.weight <= kilosToGrams(state.forecast.availableWeight));

  const isSubmitDisabled = !isValidDate(state.date) || !state.biomass;

  return (
    <form
      noValidate
      autoComplete="off"
      style={{width: '100%'}}
      onKeyDown={({key}) => {
        if (!isSubmitDisabled && key === 'Enter') {
          setCreateUpdateCageHarvest(true);
        }
      }}
    >
      <Stack direction="column" spacing={2} mb={0}>
        {/* Site Picker */}
        <GenericSelect
          label={t('site')}
          value={state.siteId ?? ''}
          onChange={(event) => {
            const siteId = event.target.value as string;
            dispatch({type: actionTypeEnum.setProperties, siteId});
          }}
          aria-label={t('site')}
          data-testid="site"
        >
          <GenericSelectItem value={undefined}>
            <em>{t('noneSelected')}</em>
          </GenericSelectItem>
          {sites?.map((site) => (
            <GenericSelectItem value={site.id} key={site.id}>
              {site.name}
            </GenericSelectItem>
          ))}
        </GenericSelect>

        {/* Cage Picker */}
        <GenericSelect
          value={state?.cageId}
          onChange={(event) => {
            const cageId = event.target.value as string;
            dispatch({type: actionTypeEnum.setProperties, cageId});
          }}
          label={t('cage')}
          aria-label={t('cage')}
          data-testid="cage"
        >
          {cages?.map((cage) => (
            <GenericSelectItem key={cage.id} value={cage.id}>
              {cage.name}
            </GenericSelectItem>
          ))}
        </GenericSelect>

        {state.cageId && (
          <GenericSelect
            value={state?.harvestPlantId ?? ''}
            onChange={(event) => {
              const harvestPlantId = event.target.value as string | undefined;
              dispatch({type: actionTypeEnum.setHarvestPlant, harvestPlantId});
            }}
            label={t('harvestPlant')}
            aria-label={t('harvestPlant')}
            data-testid="harvestPlant"
            disabled={!state.cageId}
          >
            <GenericSelectItem key="empty_value" value={undefined}>
              {t('none')}
            </GenericSelectItem>
            {availableHarvestPlants.map((harvestPlant) => (
              <GenericSelectItem key={harvestPlant.id} value={harvestPlant.id}>
                {harvestPlant.name}
              </GenericSelectItem>
            ))}
          </GenericSelect>
        )}

        {/* Sequence checkbox */}
        {state.cageId && !state.isEmergency && (
          <Stack direction={'row'} justifyContent="space-between" alignItems="center">
            <FormControlLabel
              control={
                <Checkbox
                  disableRipple
                  checked={state.isSequence}
                  disabled={!state.cageId}
                  slotProps={{
                    // @ts-expect-error -- slotProps.input is not typed to support data attributes
                    // https://github.com/mui/material-ui/issues/33175
                    input: {'data-testid': 'sequence-checkbox-input'},
                  }}
                  onChange={() => {
                    dispatch({type: actionTypeEnum.setHarvestSequence, isSequence: !state.isSequence});
                  }}
                />
              }
              label={t('sequence')}
              sx={{userSelect: 'none', height: 26}}
            />
          </Stack>
        )}

        {state.isSequence && (
          <StyledBox>
            <MultiDatePicker dispatch={dispatch} state={state} startDate={planStartDate} endDate={planEndDate} />
          </StyledBox>
        )}

        {/* Harvest Date */}
        {!state.isSequence && (
          <InputMenuDatePicker
            value={state.date}
            onChange={(date) => {
              dispatch({type: actionTypeEnum.setProperties, date});
            }}
            slotProps={{
              textField: {
                onKeyDown: (event) => {
                  inputMenuDispatch && handleKeyDown(inputMenuDispatch, event, setCreateUpdateCageHarvest);
                },
              },
            }}
          />
        )}
        {/* Availability  */}
        {/* TODO: make everything dependant on the state */}
        {state.forecast && state.availability && (
          <HarvestAvailability {...{forecast: state.forecast, state, availability: state.availability}} />
        )}

        {state.cageId && (
          <>
            {/* Full Harvest */}
            {!state.isSequence && (
              <Stack direction={'row'} justifyContent="space-between" alignItems="center" pt={0} pb={0.5}>
                <Button
                  variant="outlined"
                  disabled={state.fullHarvest || !state.forecast?.availableCount}
                  onClick={() => {
                    dispatch({type: actionTypeEnum.setFullHarvest, fullHarvest: !state.fullHarvest});
                  }}
                  data-testid={'full-harvest'}
                >
                  {t('makeHarvestFull')}
                </Button>

                <WeightUnitSelector />
              </Stack>
            )}

            {/* Harvest Biomass */}
            <GenericTextField
              slotProps={{
                input: {
                  className: AUTO_FOCUS_CLASS,
                  endAdornment: (
                    <StyledAdornedEnd>{`${t(filterContext.weightUnit ?? DEFAULT_WEIGHT_UNIT)} ${t(
                      'ton',
                    )}`}</StyledAdornedEnd>
                  ),
                  inputComponent: NumberFormatCustom as (props) => JSX.Element,
                  inputProps: {'data-testid': 'biomass'},
                },
                inputLabel: {shrink: true},
              }}
              label={t(state.isSequence ? 'biomassPerHarvest' : 'biomass')}
              value={
                state.biomass > 0
                  ? Math.round(formatLweHog(state.biomass, state.forecast?.openSpecies ?? undefined) * 100) / 100
                  : ''
              }
              onFocus={() => {
                dispatch({type: actionTypeEnum.setProperties, focusedField: 'biomass'});
              }}
              onChange={(event) => {
                if (state.focusedField === 'biomass') {
                  const biomass = formatToLwe(parseFloat(event.target.value), state.forecast?.openSpecies ?? undefined);
                  dispatch({type: actionTypeEnum.setBiomass, biomass});
                }
              }}
              disabled={!state.cageId}
              error={!state.biomass && state.typingField === 'biomass'}
            />

            {/* Harvest Count */}
            <GenericTextField
              slotProps={{
                input: {
                  endAdornment: <StyledAdornedEnd>{t('fish')}</StyledAdornedEnd>,
                  inputComponent: NumberFormatCustom as (props) => JSX.Element,
                  inputProps: {'data-testid': 'count'},
                },
                inputLabel: {shrink: true},
              }}
              label={t(state.isSequence ? 'countPerHarvest' : 'count')}
              value={state.count > 0 ? state.count.toFixed(0) : ''}
              onFocus={() => {
                dispatch({type: actionTypeEnum.setProperties, focusedField: 'count'});
              }}
              onChange={(event) => {
                if (state.focusedField === 'count') {
                  const count = parseInt(event.target.value);
                  dispatch({type: actionTypeEnum.setCount, count});
                }
              }}
              disabled={!state.cageId}
              error={!state.count && state.typingField === 'count'}
            />

            {/* Harvest Weight */}
            {!state.isSequence && (
              <>
                <GenericTextField
                  slotProps={{
                    input: {
                      sx: {opacity: state.fullHarvest ? 0.35 : undefined},
                      endAdornment: <StyledAdornedEnd>{t('grams')}</StyledAdornedEnd>,
                      inputComponent: NumberFormatCustom as (props) => JSX.Element,
                      inputProps: {
                        max: MAX_FISH_WEIGHT_GRAMS,
                        min: state.forecast?.closingWeight ?? 0,
                        'data-testid': 'weight',
                      },
                    },
                    inputLabel: {shrink: true},
                  }}
                  label={t(state.isSequence ? 'weightPerHarvest' : 'weight')}
                  error={gradedHarvestError}
                  helperText={
                    gradedHarvestError &&
                    state.forecast?.availableWeight &&
                    t('harvestWeightErrorHelperText', {
                      weight: numberDisplay(
                        kilosToGrams(
                          formatLweHog(state.forecast?.availableWeight, state.forecast?.openSpecies ?? undefined),
                        ),
                        0,
                      ),
                    })
                  }
                  value={
                    !state.fullHarvest && state.isGraded && state?.weight && state?.weight > 0
                      ? formatLweHog(state?.weight ?? 0, state.forecast?.openSpecies ?? undefined).toFixed(0)
                      : ''
                  }
                  onFocus={() => {
                    dispatch({type: actionTypeEnum.setProperties, focusedField: 'weight'});
                  }}
                  onChange={(event) => {
                    if (state.focusedField === 'weight') {
                      const weight = formatToLwe(
                        parseInt(event.target.value),
                        state.forecast?.openSpecies ?? undefined,
                      );
                      dispatch({type: actionTypeEnum.setWeight, weight});
                    }
                  }}
                  disabled={state.fullHarvest}
                />
              </>
            )}
          </>
        )}
        {state.fullHarvest && (
          <Alert severity="info" color="info" variant="standard">
            {t('fullHarvestInfoBox', {fullHarvestRate: FULL_HARVEST_PERCENTAGE * 100})}
          </Alert>
        )}
        {state.isGraded && !state.fullHarvest && (
          <Alert severity="info" color="info" variant="standard">
            {t('gradedHarvestInfoBox')}
          </Alert>
        )}

        {state.isSequence && state.forecast && (
          <TotalHarvestSequence
            biomass={state.biomass}
            datesSequence={state.datesSequence}
            availableBiomass={state.forecast.closingBiomass}
            species={state.forecast.openSpecies ?? undefined}
          />
        )}
        {!state.isEmergency && !state.isSequence && <CageHarvestRecommendations state={state} dispatch={dispatch} />}
        <InputActionButtons disabled={isSubmitDisabled} setCreateUpdateInput={setCreateUpdateCageHarvest} />
      </Stack>
    </form>
  );
};

export default EditMenuContainer;

const HarvestAvailability = ({
  state,
  forecast,
  availability,
}: {
  state: IHarvest;
  forecast: ForecastState;
  availability: IAvailability;
}) => {
  const {t} = useTranslation('common');
  const {formatLweHog} = useGetHogLweFormatter();

  const date = state?.date;
  const resourceId = date && state.cageId ? [state.cageId, date, date] : undefined;
  const {data: harvests} = useGetCageHarvests({resourceId});
  const harvestsComparison = harvests?.filter((harvest) => harvest.id !== state.id).length;
  const renderAlert = harvestsComparison ? harvestsComparison <= 1 : false;

  return (
    <Box>
      <StyledAvailabilityContainer elevation={0}>
        <Stack direction="row" spacing={1} p={1} justifyContent="space-between">
          <Box pl={1}>
            <Typography color="textSecondary" variant="body2">
              {t('openState')}
            </Typography>
            {
              <>
                {ValueLine(
                  t('ton'),
                  kilosToTons(formatLweHog(forecast.availableBiomass, state.forecast?.openSpecies ?? undefined)),
                )}
                {ValueLine(
                  t('grams'),
                  kilosToGrams(formatLweHog(forecast.availableWeight, state.forecast?.openSpecies ?? undefined)),
                )}
                {ValueLine(t('count'), forecast.availableCount)}
              </>
            }
          </Box>
          <Box>
            <Typography color="textSecondary" variant="body2">
              {t('closingState')}
            </Typography>
            {
              <>
                {ValueLine(
                  t('ton'),
                  formatLweHog(availability.closingBiomass, state.forecast?.openSpecies ?? undefined),
                  'closing-biomass',
                )}
                {ValueLine(
                  t('grams'),
                  formatLweHog(availability.closingWeight, state.forecast?.openSpecies ?? undefined),
                  'closing-weight',
                )}
                {ValueLine(t('count'), availability.closingCount, 'closing-count')}
              </>
            }
          </Box>
          <Tooltip title={t('openingClosingExplanation') ?? ''} sx={{cursor: 'pointer'}}>
            <InfoIcon />
          </Tooltip>
        </Stack>
      </StyledAvailabilityContainer>
      {renderAlert && (
        <Stack paddingTop={1}>
          <Alert severity="info" color="info" variant="standard">
            {t('otherHarvestOnSameDate')}
          </Alert>
        </Stack>
      )}
    </Box>
  );
};

const TotalHarvestSequence = ({
  biomass,
  datesSequence,
  availableBiomass,
  species,
}: {
  biomass: number;
  datesSequence: Date[];
  availableBiomass: number;
  species?: ForecastStateOpenSpeciesEnum;
}) => {
  const {t} = useTranslation('common');
  const {formatLweHog} = useGetHogLweFormatter();

  const value = biomass * datesSequence.length > availableBiomass ? availableBiomass : biomass * datesSequence.length;
  return (
    <StyledAvailabilityContainer elevation={0}>
      <Box p={1} paddingLeft={1.5}>
        <Typography color="textPrimary" variant="button" gutterBottom>
          {t('totalBiomass')}
        </Typography>
        <br />
        {ValueLine(t('ton'), formatLweHog(value, species))}
      </Box>
    </StyledAvailabilityContainer>
  );
};

const ValueLine = (unit: string, value?: number, testId?: string) => (
  <>
    <Typography variant="body2" color="textPrimary" display="inline" data-testid={testId}>
      {numberDisplay(value, 0)}
    </Typography>
    <Typography variant="body2" color="textSecondary" display="inline" style={{textTransform: 'lowercase'}}>
      {` ${unit}`}
    </Typography>
    <br />
  </>
);

const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) => prop !== 'selected',
})(({theme, selected}) => ({
  ...(selected && {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover, &:focus': {
      backgroundColor: theme.palette.primary.dark,
    },
    borderTopLeftRadius: '50%',
    borderBottomLeftRadius: '50%',
    borderTopRightRadius: '50%',
    borderBottomRightRadius: '50%',
  }),
  margin: 2,
}));

function MultiDatePicker({
  state,
  dispatch,
  startDate,
  endDate,
}: {
  state: IHarvest;
  dispatch: Dispatch<IHarvestAction>;
  startDate?: Date;
  endDate?: Date;
}) {
  const initialDate = state?.date;
  const findDate = (dates, date) => {
    const dateTime = date.getTime();
    return dates.find((item) => item.getTime() === dateTime);
  };

  const findIndexDate = (dates, date) => {
    const dateTime = date.getTime();
    return dates.findIndex((item) => item.getTime() === dateTime);
  };

  const renderPickerDay = (date, selectedDates, pickersDayProps) => {
    if (!state.datesSequence) {
      return <PickersDay {...pickersDayProps} />;
    }
    const selected = findDate(state.datesSequence, date);
    return <CustomPickersDay {...pickersDayProps} disableMargin selected={selected} />;
  };

  return (
    <StaticDatePicker
      defaultValue={initialDate}
      onChange={(newValue) => {
        const array = state.datesSequence;
        const date = startOfDay(newValue as Date);
        const index = findIndexDate(array, date);
        if (index >= 0) {
          array.splice(index, 1);
        } else {
          array.push(date);
        }
        dispatch({type: actionTypeEnum.setHarvestSequenceDates, datesSequence: array});
      }}
      minDate={startDate}
      maxDate={endDate}
      slots={{
        day: (props) => renderPickerDay(props.day, state.datesSequence, props),
      }}
      slotProps={{
        day: {},
        toolbar: {
          hidden: true,
        },
        actionBar: {actions: []},
      }}
    />
  );
}

const StyledBox = styled(Box)(({theme}) => ({
  outline: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(0.5),
  '& .PrivatePickersSlideTransition-root': {},
}));

const StyledAvailabilityContainer = styled(Paper)(({theme}) => ({
  display: 'inline-block',
  width: '100%',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(0.5),
  backgroundColor: alpha(theme.palette.background.default, 0.5),
}));
